import { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import { AlertCircle, ChevronDown, RefreshCw, Search, Users } from 'lucide-react'
import Spinner from '../components/Spinner'
import ProjectList from '../components/ProjectList'
import './Projects.css'
import { useGetProcessingSummaryQuery, useGetProjectsQuery } from '../services/projectDataApi'
import { useTeams } from '../hooks/useProjectTeam'

// The team filter's "show everything" value. A sentinel rather than null so the <select> stays a
// controlled component with a real option behind it.
const ALL_TEAMS = 'all'

export default function Projects() {
  const projectsQuery = useGetProjectsQuery({ statusFilter: 'all', page: 1, pageSize: 50 })
  const processingQuery = useGetProcessingSummaryQuery(undefined, { pollingInterval: 10_000, skipPollingIfUnfocused: true })
  const items = projectsQuery.data?.items ?? []
  const loading = projectsQuery.isLoading
  const error = projectsQuery.error ? 'Failed to fetch projects' : null
  const activeProjects = useMemo(
    () => new Set((processingQuery.data?.running ?? []).map((task) => task.project_id)),
    [processingQuery.data?.running],
  )
  const [searchQuery, setSearchQuery] = useState('')
  // Which team's projects to show. Local to this page and not persisted: a filter is a way of looking
  // at the list, not a mode the rest of the app should inherit.
  const [teamFilter, setTeamFilter] = useState<string>(ALL_TEAMS)
  const { teams } = useTeams()
  const listScrollRef = useRef<HTMLDivElement | null>(null)
  // Scroll-edge shadows: the header casts a shadow once the list is scrolled past the top, and a
  // matching shadow sits along the bottom edge while there's more content below.
  const [scrollEdges, setScrollEdges] = useState({ atTop: true, atBottom: true })

  const updateScrollEdges = useCallback(() => {
    const el = listScrollRef.current
    const atTop = !el || el.scrollTop <= 0
    const atBottom = !el || el.scrollTop + el.clientHeight >= el.scrollHeight - 1
    setScrollEdges((prev) => (prev.atTop === atTop && prev.atBottom === atBottom ? prev : { atTop, atBottom }))
  }, [])

  useEffect(() => {
    window.addEventListener('resize', updateScrollEdges)
    return () => window.removeEventListener('resize', updateScrollEdges)
  }, [updateScrollEdges])

  useEffect(() => {
    document.title = 'Projects — Replay QA'
  }, [])

  const load = useCallback(() => {
    void projectsQuery.refetch()
  }, [projectsQuery])

  const trimmedSearch = searchQuery.trim()
  // A team that no longer appears (left, or renamed away) must not silently hide every project.
  const activeTeamFilter = teams.some((team) => team.id === teamFilter) ? teamFilter : ALL_TEAMS
  const ownTeamId = teams.find((team) => team.is_owner)?.id ?? null

  const filteredProjects = useMemo(() => {
    const q = trimmedSearch.toLowerCase()
    const byTeam = activeTeamFilter === ALL_TEAMS
      ? items
      : items.filter((p) =>
          // A project created before teams has no team_id and is funded by its owner's own account, so
          // it belongs with the projects of the viewer's own team rather than nowhere.
          p.team_id ? p.team_id === activeTeamFilter : activeTeamFilter === ownTeamId,
        )
    const matches = trimmedSearch ? byTeam.filter((p) => p.name.toLowerCase().includes(q)) : [...byTeam]
    // Most recently explored first; projects that have never run sort to the end.
    return matches.sort((a, b) => {
      const aTime = a.last_test_run ? new Date(a.last_test_run).getTime() : 0
      const bTime = b.last_test_run ? new Date(b.last_test_run).getTime() : 0
      return bTime - aTime
    })
  }, [items, trimmedSearch, activeTeamFilter, ownTeamId])

  // Re-measure whenever the rendered list changes (load finishes, filter narrows, etc.).
  useEffect(() => {
    updateScrollEdges()
  }, [filteredProjects, loading, error, updateScrollEdges])

  const filtering = trimmedSearch.length > 0 || activeTeamFilter !== ALL_TEAMS
  const showNoMatches = !loading && !error && items.length > 0 && filtering && filteredProjects.length === 0
  const filteredTeamName = teams.find((team) => team.id === activeTeamFilter)?.name ?? null
  const noMatchesMessage = trimmedSearch
    ? filteredTeamName
      ? `No projects in ${filteredTeamName} match "${trimmedSearch}".`
      : `No projects match "${trimmedSearch}". Try a different name.`
    : `No projects in ${filteredTeamName ?? 'this team'} yet.`

  return (
    <div className="projects-page" data-testid="projects-page">
      <header className={`projects-page__header${scrollEdges.atTop ? '' : ' projects-page__header--scrolled'}`}>
        <div className="projects-page__header-inner">
        <div className="projects-page__heading">
          <h1 className="projects-page__title">Projects</h1>
          <p className="projects-page__subtitle">Browse and open your QA workspaces.</p>
        </div>
        <div className="projects-page__filters">
          {teams.length > 1 && (
            <div className="projects-page__team">
              <label className="projects-page__search-label" htmlFor="projects-team">
                Filter by team
              </label>
              <Users size={16} strokeWidth={1.75} className="projects-page__team-icon" aria-hidden />
              <select
                id="projects-team"
                className="projects-page__team-select"
                value={activeTeamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                data-testid="projects-team-filter"
                data-analytics-event="projects-team-filter"
              >
                <option value={ALL_TEAMS}>All teams</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                    {team.is_owner ? ' (yours)' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} strokeWidth={2} className="projects-page__team-chevron" aria-hidden />
            </div>
          )}
          <div className="projects-page__search">
          <label className="projects-page__search-label" htmlFor="projects-search">
            Search projects
          </label>
          <Search size={16} strokeWidth={1.75} className="projects-page__search-icon" aria-hidden />
          <input
            id="projects-search"
            type="search"
            className="projects-page__search-input"
            placeholder="Filter by name…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            data-testid="projects-search-input"
            data-analytics-event="projects-search-input"
          />
          </div>
        </div>
        </div>
      </header>

      {loading && items.length === 0 ? (
        <div className="projects-page__fill">
          <div className="projects-page__loading" role="status" aria-live="polite" data-testid="projects-loading">
            <Spinner size={22} className="projects-page__spinner" />
            Loading projects…
          </div>
        </div>
      ) : error ? (
        <div className="projects-page__fill">
          <div className="projects-page__error" role="alert" data-testid="projects-error">
            <AlertCircle size={20} aria-hidden />
            <p>{error}</p>
            <button type="button" className="projects-page__retry" onClick={load} data-testid="projects-retry" data-analytics-event="projects-error-retry">
              <RefreshCw size={14} aria-hidden />
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="projects-page__list-wrap">
          <div className="projects-page__list-scroll" ref={listScrollRef} onScroll={updateScrollEdges}>
            <div className="projects-page__list-inner">
              {showNoMatches ? (
                <p className="projects-page__no-results" role="status" data-testid="projects-no-results">
                  {noMatchesMessage}
                </p>
              ) : (
                <ProjectList projects={filteredProjects} activeProjectIds={activeProjects} />
              )}
            </div>
          </div>
          <div
            className={`projects-page__bottom-shadow${scrollEdges.atBottom ? '' : ' projects-page__bottom-shadow--visible'}`}
            aria-hidden
          />
        </div>
      )}
    </div>
  )
}
